/**
 * Tree integrity (SPEC §5.2). One pure function used by every client and by the DO.
 *
 * - Cycles (from concurrent parentId edits) are broken deterministically: the cycle
 *   member with the greatest (updatedAt, id) is treated as top level.
 * - Orphans (parent missing, or parent trashed while the child is not) render under
 *   the Inbox. No data is mutated; the next user edit fixes things up for real.
 * - Archive/trash state flows down: a hidden parent hides its subtree.
 */
import { compareOrder } from './order'
import { INBOX_ID } from './types'

export interface TreeNodeInput {
  id: string
  parentId: string | null
  order: string
  updatedAt: number
  archivedAt?: number
  trashedAt?: number
  system?: string
}

export interface ResolvedTree {
  /** Effective parent after cycle breaking and orphan re-homing (null = top level). */
  parentOf: Map<string, string | null>
  /** Children sorted by (order, id), including hidden ones. Key null = top level. */
  childrenOf: Map<string | null, string[]>
  depthOf: Map<string, number>
  /** Trashed, or inside a trashed subtree. */
  inTrash: Set<string>
  /** Archived or inside an archived subtree (and not in the trash). */
  inArchive: Set<string>
  /** Rendered under the Inbox because the stored parent is missing or trashed. */
  orphans: Set<string>
  /** Items whose stored parentId was ignored to break a cycle. */
  cycleBreaks: Set<string>
}

const EMPTY: string[] = []

export function resolveTree(items: ReadonlyMap<string, TreeNodeInput> | Iterable<TreeNodeInput>): ResolvedTree {
  const byId: ReadonlyMap<string, TreeNodeInput> =
    items instanceof Map ? items : new Map([...(items as Iterable<TreeNodeInput>)].map((i) => [i.id, i]))

  const parentOf = new Map<string, string | null>()
  const orphans = new Set<string>()
  const cycleBreaks = new Set<string>()
  const hasInbox = byId.has(INBOX_ID)

  // 1. Stored parent → effective parent (system items, self-loops, orphans).
  for (const item of byId.values()) {
    const p = item.parentId
    if (item.system || p === null || p === undefined || p === '') {
      parentOf.set(item.id, null)
    } else if (p === item.id) {
      parentOf.set(item.id, null)
      cycleBreaks.add(item.id)
    } else {
      const parent = byId.get(p)
      if (!parent || (parent.trashedAt && !item.trashedAt)) {
        orphans.add(item.id)
        parentOf.set(item.id, hasInbox && item.id !== INBOX_ID ? INBOX_ID : null)
      } else {
        parentOf.set(item.id, p)
      }
    }
  }

  // 2. Break cycles. In a parent-pointer graph cycles are disjoint, so the result
  //    does not depend on iteration order.
  const state = new Map<string, 1 | 2>()
  for (const id of byId.keys()) {
    if (state.get(id) === 2) continue
    const path: string[] = []
    let cur: string | null = id
    while (cur !== null && state.get(cur) !== 2) {
      if (state.get(cur) === 1) {
        const cycle = path.slice(path.indexOf(cur))
        let breaker = cycle[0]!
        for (const c of cycle) {
          const a = byId.get(c)!
          const b = byId.get(breaker)!
          if (a.updatedAt > b.updatedAt || (a.updatedAt === b.updatedAt && c > breaker)) breaker = c
        }
        parentOf.set(breaker, null)
        cycleBreaks.add(breaker)
        break
      }
      state.set(cur, 1)
      path.push(cur)
      cur = parentOf.get(cur) ?? null
    }
    for (const p of path) state.set(p, 2)
  }

  // 3. Children lists.
  const childrenOf = new Map<string | null, string[]>()
  for (const [id, p] of parentOf) {
    let list = childrenOf.get(p)
    if (!list) childrenOf.set(p, (list = []))
    list.push(id)
  }
  for (const list of childrenOf.values()) {
    list.sort((a, b) => compareOrder(byId.get(a)!, byId.get(b)!))
  }
  // Inbox always first among top-level items, then other pinned/system items are a view concern.

  // 4. Depth + inherited hidden state.
  const depthOf = new Map<string, number>()
  const inTrash = new Set<string>()
  const inArchive = new Set<string>()
  const stack: Array<[string, number, boolean, boolean]> = []
  for (const id of childrenOf.get(null) ?? EMPTY) stack.push([id, 0, false, false])
  while (stack.length) {
    const [id, depth, pTrash, pArchive] = stack.pop()!
    const item = byId.get(id)!
    depthOf.set(id, depth)
    const trash = pTrash || !!item.trashedAt
    const archive = !trash && (pArchive || !!item.archivedAt)
    if (trash) inTrash.add(id)
    if (archive) inArchive.add(id)
    for (const c of childrenOf.get(id) ?? EMPTY) stack.push([c, depth + 1, trash, archive])
  }

  return { parentOf, childrenOf, depthOf, inTrash, inArchive, orphans, cycleBreaks }
}

export function isHidden(tree: ResolvedTree, id: string): boolean {
  return tree.inTrash.has(id) || tree.inArchive.has(id)
}

export function childrenOf(tree: ResolvedTree, id: string | null): readonly string[] {
  return tree.childrenOf.get(id) ?? EMPTY
}

/** Ancestors from the direct parent up to the top-level item. */
export function ancestorsOf(tree: ResolvedTree, id: string): string[] {
  const out: string[] = []
  let cur = tree.parentOf.get(id) ?? null
  let guard = 0
  while (cur !== null && guard++ < 10000) {
    out.push(cur)
    cur = tree.parentOf.get(cur) ?? null
  }
  return out
}

export function topLevelOf(tree: ResolvedTree, id: string): string {
  const anc = ancestorsOf(tree, id)
  return anc.length ? anc[anc.length - 1]! : id
}

/** Is `ancestor` a strict ancestor of `id`? */
export function isAncestor(tree: ResolvedTree, ancestor: string, id: string): boolean {
  let cur = tree.parentOf.get(id) ?? null
  let guard = 0
  while (cur !== null && guard++ < 10000) {
    if (cur === ancestor) return true
    cur = tree.parentOf.get(cur) ?? null
  }
  return false
}

/** SPEC §5.2: refuse any drop that would put an item under its own descendant. */
export function wouldCreateCycle(tree: ResolvedTree, id: string, newParentId: string | null): boolean {
  if (newParentId === null) return false
  return newParentId === id || isAncestor(tree, id, newParentId)
}

/** All descendants (depth-first, in child order). */
export function descendantsOf(tree: ResolvedTree, id: string): string[] {
  const out: string[] = []
  const stack = [...(tree.childrenOf.get(id) ?? EMPTY)].reverse()
  while (stack.length) {
    const cur = stack.pop()!
    out.push(cur)
    const kids = tree.childrenOf.get(cur)
    if (kids) for (let i = kids.length - 1; i >= 0; i--) stack.push(kids[i]!)
  }
  return out
}

/** Validate structural invariants (tests + diagnostics). Returns problems found. */
export function checkTree(tree: ResolvedTree, ids: Iterable<string>): string[] {
  const problems: string[] = []
  const all = [...ids]
  const seen = new Set<string>()
  for (const id of all) {
    if (!tree.parentOf.has(id)) problems.push(`missing parent entry for ${id}`)
    if (!tree.depthOf.has(id)) problems.push(`unreachable ${id}`)
    const anc = ancestorsOf(tree, id)
    if (anc.includes(id)) problems.push(`cycle through ${id}`)
  }
  for (const list of tree.childrenOf.values()) for (const c of list) {
    if (seen.has(c)) problems.push(`duplicate child ${c}`)
    seen.add(c)
  }
  if (seen.size !== all.length) problems.push(`child count ${seen.size} != item count ${all.length}`)
  return problems
}
